import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';
import API from 'api/API';
import { FileStatusMapping, FileUploadListResponse, FilterStatus } from 'api/Responses/OrderResponses';
import { SortStatus } from 'api/Responses/SetResponses';
import { DATE_PROPERTIES } from 'utils/helpers/constants';

export type StatusFilter = FilterStatus | 'all';

interface UseDeliveryOrdersList {
  loading: boolean;
  uploads: FileUploadListResponse[];
  filter: StatusFilter;
  setFilter: Dispatch<SetStateAction<StatusFilter>>;
  sort: SortStatus;
  setSort: Dispatch<SetStateAction<SortStatus>>;
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  refresh: boolean;
  setRefresh: Dispatch<SetStateAction<boolean>>;
}

const useDeliveryOrdersList = (): UseDeliveryOrdersList => {
  const [loading, setLoading] = useState(false);
  const [uploads, setUploads] = useState<FileUploadListResponse[]>([]);
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [sort, setSort] = useState<SortStatus>(SortStatus.NEWEST);
  const [search, setSearch] = useState<string>('');
  const [refresh, setRefresh] = useState<boolean>(true);

  const sortAscending = (objA: FileUploadListResponse, objB: FileUploadListResponse) => {
    const submissionDateA = objA.created ? new Date(objA.created) : new Date(DATE_PROPERTIES.MAX_VALUE);
    const submissionDateB = objB.created ? new Date(objB.created) : new Date(DATE_PROPERTIES.MAX_VALUE);

    return submissionDateA.getTime() - submissionDateB.getTime();
  };

  const sortDescending = (objA: FileUploadListResponse, objB: FileUploadListResponse) => {
    const submissionDateA = objA.created ? new Date(objA.created) : new Date(DATE_PROPERTIES.MIN_VALUE);
    const submissionDateB = objB.created ? new Date(objB.created) : new Date(DATE_PROPERTIES.MIN_VALUE);

    return submissionDateB.getTime() - submissionDateA.getTime();
  };

  const filteredUploads = useMemo(() => {
    const result =
      filter && filter !== 'all'
        ? uploads.filter(upload => {
            const isAllowed = FileStatusMapping[filter].includes(upload.status);
            return isAllowed && upload;
          })
        : uploads;

    return result;
  }, [filter, uploads]);

  const searchedUploads = useMemo(() => {
    const regexSearchQuery = new RegExp(search, 'i');
    if (search.length >= 3) {
      return filteredUploads.filter(upload => upload.filename.search(regexSearchQuery) !== -1);
    } else {
      return filteredUploads;
    }
  }, [filteredUploads, search]);

  const sortUploads = useMemo(() => {
    switch (sort) {
      case SortStatus.NEWEST: {
        return searchedUploads.sort((a, b) => sortDescending(a, b));
      }
      case SortStatus.OLDEST: {
        return searchedUploads.sort((a, b) => sortAscending(a, b));
      }
    }
  }, [searchedUploads, sort]);

  // Fetch all drafts from the DB
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        if (refresh) {
          const response = await API.orders.getFileUploadHistoryList();
          setUploads(response);
          setRefresh(false);
        }
      } catch (error) {
        API.errorHandler(error);
      }
      setLoading(false);
    })();
  }, [refresh]);

  return {
    loading,
    uploads: sortUploads,
    filter,
    setFilter,
    sort,
    setSort,
    search,
    setSearch,
    refresh,
    setRefresh,
  };
};

export default useDeliveryOrdersList;
